// @ts-strict-ignore
import {renamePropertyValue} from 'common/report/util/properties';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {KeyedByDate, KeyedBySegment, SegmentKeyToPath} from 'common/report/retention/models/types';
import {LocalizationStore} from 'common/i18n/store';

import {getGroupByName} from './format';

function flatten(
  segmentGroups: Segment[],
  segments: KeyedBySegment<any>,
  depth: number,
  i18n: LocalizationStore,
  prefix = null,
  pathAccum = [],
): {flattened: KeyedBySegment<any>; segmentKeyToPath: SegmentKeyToPath} {
  const flattened = {};
  const segmentKeyToPath = {};
  for (const [segment, data] of Object.entries(segments)) {
    const renamedSegment = renamePropertyValue(
      segment,
      getGroupByName(segmentGroups[segmentGroups.length - depth], i18n),
    );
    const key = prefix ? `${prefix} / ${renamedSegment}` : renamedSegment;
    const path = [...pathAccum, renamedSegment];
    if (depth === 1) {
      flattened[key] = data;
      segmentKeyToPath[key] = path;
    } else {
      const {flattened: recursiveFlattened, segmentKeyToPath: recursivePath} = flatten(
        segmentGroups,
        data,
        depth - 1,
        i18n,
        key,
        path,
      );
      Object.assign(flattened, recursiveFlattened);
      Object.assign(segmentKeyToPath, recursivePath);
    }
  }

  return {flattened, segmentKeyToPath};
}

/**
 *
 * Transform the API response format with a multiseg tree to a tree with exactly 3 levels
 * date, flattened segments, retention/addiction data. Also returns an object for retrieving
 * the original segment path from the flattened segment string.
 **/
export function flattenSegments(
  response: KeyedByDate<any>,
  segmentGroups: Segment[],
  i18n: LocalizationStore,
): {responseData: KeyedByDate<any>; segmentKeyToPath: SegmentKeyToPath} {
  const responseData = {};
  let segmentKeyToPath = {};

  for (const [date, segments] of Object.entries(response)) {
    const {flattened, segmentKeyToPath: path} = flatten(segmentGroups, segments, segmentGroups.length, i18n);
    responseData[date] = flattened;
    segmentKeyToPath = {...segmentKeyToPath, ...path};
  }

  return {responseData, segmentKeyToPath};
}

/**
 * Transform the API response format with a multi metric tree to a tree with exactly 3 levels
 * date, flattened metrics and segments (if there are any), retention/addiction data. Also returns an object for retrieving
 * the original segment path from the flattened segment string.
 **/
export function flattenMultipleRetentionMetrics(
  series: KeyedByDate<any>,
  segmentGroups: Segment[],
  i18n: LocalizationStore,
): {responseData: KeyedByDate<any>; segmentKeyToPath: SegmentKeyToPath} {
  const responseData = {};
  let segmentKeyToPath = {};
  for (const [metric, response] of Object.entries(series)) {
    for (const [date, segments] of Object.entries(response)) {
      if (segmentGroups.length === 0) {
        responseData[date] = {
          ...responseData[date],
          [metric]: segments,
        };
        segmentKeyToPath[metric] = [metric];
      } else {
        const {flattened, segmentKeyToPath: path} = flatten(
          segmentGroups,
          segments,
          segmentGroups.length,
          i18n,
          metric,
          [metric],
        );
        responseData[date] = {...responseData[date], ...flattened};
        segmentKeyToPath = {...segmentKeyToPath, ...path};
      }
    }
  }

  return {responseData, segmentKeyToPath};
}
